import { ROUTES } from "../../constants";

/**
 * Deep links out of a scorecard cell.
 *
 * The map owns its URL vocabulary in pages/publicMap/urlState/mapUrlState.js.
 * Nothing here should invent a key or a value: scorecardLinks.test.js round
 * trips every link through the map's own readRawMapUrl, so a change on that
 * side fails here rather than silently producing a link the map ignores.
 */

/** The map's graph mode, as the `mode` query value spells it. */
const GRAPH_MODE_VALUE = "wq";

/**
 * The scorecard's statistic toggle and the map's analysis filter mean the same
 * two things but spell the percentile differently.
 */
const ANALYSIS_BY_STATISTIC = {
  median: "median",
  pctile: "85th-percentile",
};

export function analysisForStatistic(statistic) {
  return ANALYSIS_BY_STATISTIC[statistic] ?? ANALYSIS_BY_STATISTIC.median;
}

/**
 * Opens the interactive map in graph mode, zoomed to one site with one
 * parameter selected, matching the flow condition and statistic the scorecard
 * is showing, with the results panel open.
 */
export function buildMapLink({ locationid, parameterid, flow, statistic }) {
  const params = new URLSearchParams();
  params.set("mode", GRAPH_MODE_VALUE);
  params.set("colorBy", "wqData");
  if (parameterid != null) params.set("params", String(parameterid));
  if (flow) params.set("flow", flow);
  params.set("analysis", analysisForStatistic(statistic));
  if (locationid != null) params.set("loc", String(locationid));
  params.set("panel", "1");

  return `${ROUTES.MAP}?${params.toString()}`;
}

/**
 * Neighbourhood scale: close enough to see which reach a site sits on, wide
 * enough to keep its neighbours in view.
 */
const FLY_TO_ZOOM = 14;

/**
 * Centers the map on a site in explore mode.
 *
 * It moves the camera rather than selecting the site, because `loc` is a
 * graph-mode-only key and would be dropped here. That is the right trade:
 * explore mode cannot color by water quality at all, so this link can never
 * contradict the shading the scorecard is showing, whichever period is
 * selected. The per-cell graph-mode link is the one that has to be careful.
 */
export function buildMapFlyToLink({ lat, lon, zoom = FLY_TO_ZOOM }) {
  if (lat === null || lat === undefined || lon === null || lon === undefined) {
    return null;
  }
  const params = new URLSearchParams();
  params.set("map", `${zoom}/${Number(lat)}/${Number(lon)}`);
  return `${ROUTES.MAP}?${params.toString()}`;
}

/**
 * Time Series Comparison does not read query parameters today, so the cell
 * links to the page and the user re-picks the site and parameter there.
 * Give it a URL vocabulary and this should pass site and parameter through.
 */
export function buildTimeSeriesLink() {
  return ROUTES.TIME_SERIES_COMPARISON;
}

export default buildMapLink;
